import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRoleUsers } from '../hooks/useRoleUsers';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import ProductModal from '../components/ProductModal';
import SentToProducerModal from '../components/SentToProducerModal';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Plus, FileSpreadsheet, X, Pencil, Trash2, Upload, Brain, AlertTriangle, CheckCircle, RefreshCw, Palette, FolderTree } from 'lucide-react';
import * as XLSX from 'xlsx';
import { sortSizeEntries } from '../lib/sizeSort';

// Renk tipi
interface Color {
  _id: string;
  code: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
}

// Kategori tipi
interface Category {
  _id: string;
  code: string;
  name: string;
  productCodeParam: string;
  isActive: boolean;
  sortOrder: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showSentToProducerModal, setShowSentToProducerModal] = useState(false);
  const [selectedProductForProducer, setSelectedProductForProducer] = useState<Product | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [showModelCodeUpload, setShowModelCodeUpload] = useState(false);
  const [modelCodeUploading, setModelCodeUploading] = useState(false);
  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: ''
  });

  // Ticimax sync state
  const [syncing, setSyncing] = useState(false);
  
  // Renk yönetimi state
  const [showColorModal, setShowColorModal] = useState(false);
  const [colors, setColors] = useState<Color[]>([]);
  const [loadingColors, setLoadingColors] = useState(false);
  const [editingColor, setEditingColor] = useState<Color | null>(null);
  const [colorFormData, setColorFormData] = useState({ code: '', name: '', sortOrder: 0 });
  const [importing, setImporting] = useState(false);
  
  // Kategori yönetimi state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({ code: '', name: '', productCodeParam: '', sortOrder: 0 });
  const [importingCategories, setImportingCategories] = useState(false);
  
  // AI Analiz state
  const [aiStats, setAiStats] = useState({
    totalProducts: 0,
    scannedProducts: 0,
    consistentProducts: 0,
    inconsistentProducts: 0,
    inconsistencyRate: 0,
    averageScore: 0,
    lastScanDate: null as string | null,
  });
  const [loadingAiStats, setLoadingAiStats] = useState(false);
  const [aiScanning, setAiScanning] = useState(false);
  
  // Renk yönetimi fonksiyonları
  const loadColors = async () => {
    try {
      setLoadingColors(true);
      const response = await axios.get('/colors');
      setColors(response.data);
    } catch (error) {
      toast.error('Renkler yüklenemedi!');
    } finally {
      setLoadingColors(false);
    }
  };

  const handleColorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingColor) {
        await axios.put(`/colors/${editingColor._id}`, colorFormData);
        toast.success('Renk güncellendi!');
      } else {
        await axios.post('/colors', colorFormData);
        toast.success('Renk eklendi!');
      }
      setEditingColor(null);
      setColorFormData({ code: '', name: '', sortOrder: 0 });
      loadColors();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız!');
    }
  };

  const handleColorDelete = async (colorId: string) => {
    if (window.confirm('Bu rengi silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`/colors/${colorId}`);
        toast.success('Renk silindi!');
        loadColors();
      } catch (error) {
        toast.error('Renk silinemedi!');
      }
    }
  };

  const handleColorEdit = (color: Color) => {
    setEditingColor(color);
    setColorFormData({ code: color.code, name: color.name, sortOrder: color.sortOrder });
  };

  const handleColorToggleActive = async (color: Color) => {
    try {
      await axios.put(`/colors/${color._id}`, { isActive: !color.isActive });
      toast.success(color.isActive ? 'Renk pasif yapıldı' : 'Renk aktif yapıldı');
      loadColors();
    } catch (error) {
      toast.error('İşlem başarısız!');
    }
  };

  const handleSeedColors = async () => {
    try {
      const response = await axios.post('/colors/seed');
      toast.success(`${response.data.created} yeni renk eklendi`);
      loadColors();
    } catch (error) {
      toast.error('Varsayılan renkler yüklenemedi!');
    }
  };

  const openColorModal = () => {
    loadColors();
    setShowColorModal(true);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Dosya tipini kontrol et
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls'); 
    const isCsv = file.name.endsWith('.csv') || file.name.endsWith('.txt');
    
    if (!isExcel && !isCsv) {
      toast.error('Lütfen Excel (.xlsx, .xls) veya CSV (.csv, .txt) dosyası seçin!');
      return;
    }

    try {
      setImporting(true);
      toast.loading('Dosya okunuyor...', { id: 'import' });

      let textData = '';

      if (isExcel) {
        // Excel dosyasını parse et
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // İlk sheet'i al
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Sheet'i tab-separated text'e çevir
        textData = XLSX.utils.sheet_to_csv(worksheet, { FS: '\t' });
      } else {
        // CSV/TXT dosyasını direkt oku
        textData = await file.text();
      }
      
      const response = await axios.post('/colors/import', {
        data: textData,
        clearExisting: false
      });
      
      const result = response.data;
      toast.success(
        `Import tamamlandı! Yeni: ${result.created}, Güncellenen: ${result.updated}` +
        (result.skipped > 0 ? `, Atlanan: ${result.skipped}` : ''),
        { id: 'import' }
      );
      
      if (result.errors?.length > 0) {
        console.warn('Import hataları:', result.errors);
      }
      
      loadColors();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Import başarısız!', { id: 'import' });
    } finally {
      setImporting(false);
      // Input'u sıfırla (aynı dosyayı tekrar seçebilmek için)
      event.target.value = '';
    }
  };

  // Kategori yönetimi fonksiyonları
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get('/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Kategoriler yüklenemedi!');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`/categories/${editingCategory._id}`, categoryFormData);
        toast.success('Kategori güncellendi!');
      } else {
        await axios.post('/categories', categoryFormData);
        toast.success('Kategori eklendi!');
      }
      setEditingCategory(null);
      setCategoryFormData({ code: '', name: '', productCodeParam: '', sortOrder: 0 });
      loadCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız!');
    }
  };

  const handleCategoryDelete = async (categoryId: string) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`/categories/${categoryId}`);
        toast.success('Kategori silindi!');
        loadCategories();
      } catch (error) {
        toast.error('Kategori silinemedi!');
      }
    }
  };

  const handleCategoryEdit = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({ 
      code: category.code, 
      name: category.name, 
      productCodeParam: category.productCodeParam,
      sortOrder: category.sortOrder 
    });
  };

  const handleCategoryToggleActive = async (category: Category) => {
    try {
      await axios.put(`/categories/${category._id}`, { isActive: !category.isActive });
      toast.success(category.isActive ? 'Kategori pasif yapıldı' : 'Kategori aktif yapıldı');
      loadCategories();
    } catch (error) {
      toast.error('İşlem başarısız!');
    }
  };

  const handleSeedCategories = async () => {
    try {
      const response = await axios.post('/categories/seed');
      toast.success(`${response.data.created} yeni kategori eklendi`);
      loadCategories();
    } catch (error) {
      toast.error('Varsayılan kategoriler yüklenemedi!');
    }
  };

  const handleCategoryFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Dosya tipini kontrol et
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls'); 
    const isCsv = file.name.endsWith('.csv') || file.name.endsWith('.txt');
    
    if (!isExcel && !isCsv) {
      toast.error('Lütfen Excel (.xlsx, .xls) veya CSV (.csv, .txt) dosyası seçin!');
      return;
    }

    try {
      setImportingCategories(true);
      toast.loading('Dosya okunuyor...', { id: 'category-import' });

      let textData = '';

      if (isExcel) {
        // Excel dosyasını parse et
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // İlk sheet'i al
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Sheet'i tab-separated text'e çevir
        textData = XLSX.utils.sheet_to_csv(worksheet, { FS: '\t' });
      } else {
        // CSV/TXT dosyasını direkt oku
        textData = await file.text();
      }
      
      const response = await axios.post('/categories/import', {
        data: textData,
        clearExisting: false
      });
      
      const result = response.data;
      toast.success(
        `Import tamamlandı! Yeni: ${result.created}, Güncellenen: ${result.updated}` +
        (result.skipped > 0 ? `, Atlanan: ${result.skipped}` : ''),
        { id: 'category-import' }
      );
      
      if (result.errors?.length > 0) {
        console.warn('Import hataları:', result.errors);
      }
      
      loadCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Import başarısız!', { id: 'category-import' });
    } finally {
      setImportingCategories(false);
      // Input'u sıfırla (aynı dosyayı tekrar seçebilmek için)
      event.target.value = '';
    }
  };

  // Ticimax ürün senkronizasyonu (ADMIN ONLY)
  const handleTicimaxSync = async () => {
    try {
      setSyncing(true);
      toast.loading('Ticimax\'tan tüm ürünler çekiliyor... (Bu işlem birkaç dakika sürebilir)', { id: 'ticimax-sync' });

      const response = await axios.post('/ticimax/sync-products');

      if (response.data.status === 'success') {
        const result = response.data.result;
        toast.success(
          `✅ Senkronizasyon tamamlandı!\n` +
          `Toplam: ${result.totalFetched}\n` +
          `Yeni: ${result.created}\n` +
          `Güncellenen: ${result.updated}\n` +
          `Hata: ${result.errors}`,
          { id: 'ticimax-sync', duration: 6000 }
        );
      }
    } catch (error: any) {
      console.error('Ticimax sync error:', error);
      toast.error(`Senkronizasyon hatası: ${error.response?.data?.message || error.message}`, { id: 'ticimax-sync' });
    } finally {
      setSyncing(false);
    }
  };

  // AI İstatistiklerini Yükle
  const loadAIStats = async () => {
    try {
      setLoadingAiStats(true);
      const response = await axios.get('/openai/stats');
      setAiStats({
        totalProducts: response.data.totalProducts || 0,
        scannedProducts: response.data.scannedProducts || 0,
        consistentProducts: response.data.consistentProducts || 0,
        inconsistentProducts: response.data.inconsistentProducts || 0,
        inconsistencyRate: response.data.inconsistencyRate || 0,
        averageScore: response.data.averageScore || 0,
        lastScanDate: response.data.lastScanDate || null,
      });
    } catch (error) {
      console.error('AI stats yüklenemedi:', error);
    } finally {
      setLoadingAiStats(false);
    }
  };

  // AI Toplu Tarama Başlat (ADMIN ONLY)
  const handleAIScan = async (forceRescan: boolean = false) => {
    try {
      setAiScanning(true);
      const scanType = forceRescan ? 'Tüm ürünler yeniden taranıyor' : 'Yeni ürünler taranıyor';
      toast.loading(`${scanType}... (Bu işlem birkaç dakika sürebilir)`, { id: 'ai-scan' });

      const response = await axios.post('/openai/scan-all', {
        forceRescan,
        limit: 0
      });

      if (response.data.status === 'success') {
        const result = response.data.result;
        toast.success(
          `✅ AI Taraması tamamlandı!\n` +
          `Taranan: ${result.processed}\n` +
          `Tutarsız: ${result.inconsistent}\n` +
          `Hata: ${result.errors}`,
          { id: 'ai-scan', duration: 6000 }
        );
        // İstatistikleri güncelle
        loadAIStats();
      }
    } catch (error: any) {
      console.error('AI scan error:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`AI Tarama hatası: ${errorMessage}`, { id: 'ai-scan' });
    } finally {
      setAiScanning(false);
    }
  };

  // AI Kontrol Edilmemiş Ürünleri Tara (ADMIN ONLY)
  const handleAIScanUncontrolled = async () => {
    try {
      setAiScanning(true);
      toast.loading('Kontrol edilmemiş ürünler taranıyor... (Bu işlem birkaç dakika sürebilir)', { id: 'ai-scan-uncontrolled' });

      const response = await axios.post('/openai/scan-uncontrolled', {
        limit: 0
      });

      if (response.data.status === 'success') {
        const result = response.data.result;
        toast.success(
          `✅ AI Taraması tamamlandı!\n` +
          `Taranan: ${result.totalScanned}\n` +
          `Tutarsız: ${result.inconsistent}\n` +
          `Hata: ${result.errors}`,
          { id: 'ai-scan-uncontrolled', duration: 6000 }
        );
        // İstatistikleri güncelle
        loadAIStats();
      }
    } catch (error: any) {
      console.error('AI scan uncontrolled error:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`AI Tarama hatası: ${errorMessage}`, { id: 'ai-scan-uncontrolled' });
    } finally {
      setAiScanning(false);
    }
  };

  useEffect(() => {
    loadProducts();
    loadAIStats();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      const response = await axios.get('/products');
      
      // Admin tüm ürünleri görebilir
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    }
  };
  
  const handleSentToProducer = (productId: string) => {
    const product = products.find(p => p._id === productId);
    if (product) {
      setSelectedProductForProducer(product);
      setShowSentToProducerModal(true);
    }
  };

  const handleSentToProducerSubmit = async (data: { purchaseCode: string; referenceSku: string }) => {
    if (!selectedProductForProducer) return;

    try {
      const isProducerStep = selectedProductForProducer.status === 'barcode_sent';
      const response = await axios.post(
        `/products/${selectedProductForProducer._id}/${isProducerStep ? 'producer-sent' : 'sent-to-producer'}`,
        {
          productionInfo: {
            purchaseCode: data.purchaseCode,
            referenceSku: data.referenceSku
          }
        }
      );
      
      if (response.data) {
        toast.success(isProducerStep ? 'Ürün üreticiye gönderildi ve stok girişine iletildi!' : 'Ürün üreticiye gönderildi!');
        setShowSentToProducerModal(false);
        setSelectedProductForProducer(null);
        loadProducts();
      }
    } catch (error) {
      toast.error('İşlem başarısız oldu!');
      console.error('Sent to producer error:', error);
      throw error;
    }
  };
  
  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      const response = await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason,
        rejectedBy: user?.id
      });
      
      if (response.data) {
        toast.success('Ürün başarıyla geri yollandı');
        loadProducts();
      }
    } catch (error) {
      toast.error('Geri yollama işlemi başarısız!');
      console.error('Reject error:', error);
      throw error;
    }
  };

  // Yayına Al — /publish endpoint rol + status guard'larını kendisi yapıyor
  const handlePublish = async (productId: string) => {
    try {
      await axios.post(`/products/${productId}/publish`, {
        notes: 'Ürün yayına alındı.'
      });
      toast.success('✅ Ürün başarıyla yayınlandı!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Yayınlama başarısız!');
      console.error('Publish error:', error);
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  // Ürün silme işlemleri (sadece admin)
  const handleDeleteClick = (product: Product) => {
    // Edge case: User null/undefined kontrolü
    if (!user) {
      toast.error('Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    // Edge case: Yetki kontrolü
    if (user.role !== 'admin') {
      toast.error('Bu işlem için admin yetkisi gerekiyor!');
      return;
    }

    // Edge case: Product null/undefined kontrolü
    if (!product || !product._id) {
      toast.error('Geçersiz ürün bilgisi!');
      return;
    }

    // Edge case: MongoDB ObjectId format kontrolü (24 karakter)
    if (product._id.length !== 24) {
      toast.error('Geçersiz ürün ID formatı!');
      return;
    }

    setProductToDelete(product);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    // Edge case: Null check
    if (!productToDelete) {
      toast.error('Silinecek ürün bulunamadı!');
      return;
    }

    // Edge case: ID validasyonu
    if (!productToDelete._id || productToDelete._id.trim() === '') {
      toast.error('Geçersiz ürün ID!');
      setShowDeleteDialog(false);
      setProductToDelete(null);
      return;
    }

    try {
      toast.loading('Ürün siliniyor...', { id: 'delete-product' });

      await axios.delete(`/products/${productToDelete._id}`, {
        timeout: 10000 // 10 saniye timeout
      });

      const productName = productToDelete.productionInfo?.productName || 'İsimsiz Ürün';
      toast.success(`✅ Ürün başarıyla silindi: ${productName}`, { id: 'delete-product' });

      // State'i güncelle
      setShowDeleteDialog(false);
      setProductToDelete(null);
      
      // Edge case: Ürünler listesini güvenli şekilde yenile
      try {
        await loadProducts();
      } catch (refreshError) {
        console.warn('Liste yenilenirken hata:', refreshError);
        // Silme başarılı oldu ama refresh başarısız - kullanıcıya bildir
        toast('⚠️ Liste yenilenirken hata oluştu. Sayfayı yenileyin.', { duration: 3000 });
      }

    } catch (error: any) {
      console.error('❌ Delete error:', error);

      let errorMessage = 'Ürün silinemedi!';

      if (error.response) {
        // Backend'den gelen hata mesajı
        errorMessage = error.response.data?.message || error.message;

        // HTTP status kodlarına göre özel mesajlar
        switch (error.response.status) {
          case 400:
            errorMessage = 'Geçersiz ürün bilgisi!';
            break;
          case 403:
            errorMessage = 'Bu işlem için yetkiniz yok!';
            break;
          case 404:
            errorMessage = 'Ürün bulunamadı! Zaten silinmiş olabilir.';
            break;
          case 500:
            errorMessage = 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
            break;
          case 503:
            errorMessage = 'Veritabanına bağlanılamadı. Lütfen daha sonra tekrar deneyin.';
            break;
        }
      } else if (error.request) {
        // Network hatası
        errorMessage = 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.';
      } else if (error.code === 'ECONNABORTED') {
        // Timeout hatası
        errorMessage = 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
      }

      toast.error(errorMessage, { id: 'delete-product', duration: 5000 });
      
      // Edge case: Hata durumunda dialog'u kapatma - kullanıcı tekrar deneyebilsin
      // setShowDeleteDialog(false); // Yorumda bırakıldı
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setProductToDelete(null);
  };

  const applyFilters = () => {
    let filtered = [...products];

    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }

    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    if (filters.department) {
      if (filters.department === 'publish_pending') {
        // TODO: migration sonrası image_upload+uploadedExternally OR koşulu kaldırılacak
        filtered = filtered.filter(p =>
          p.status === 'publish_pending' ||
          (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
        );
      } else if (filters.department === 'image_upload') {
        // Görsel kartı: legacy (uploadedExternally=true) olanları hariç tut — onlar publish_pending'de sayılıyor
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.department === 'published') {
        filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
      } else {
        filtered = filtered.filter(p => p.status === filters.department);
      }
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.productionInfo?.productName?.toLowerCase().includes(searchLower) ||
        p.nebimInfo?.sku?.toLowerCase().includes(searchLower) ||
        p._id.toLowerCase().includes(searchLower)
      );
    }

    setFilteredProducts(filtered);
  };

  // Model Kodu Excel Yükleme
  const handleModelCodeExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setModelCodeUploading(true);
    toast.loading('Excel okunuyor...', { id: 'model-code-upload' });

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet);

      if (rows.length === 0) {
        toast.error('Excel dosyası boş!', { id: 'model-code-upload' });
        setModelCodeUploading(false);
        return;
      }

      const items = rows
        .filter((row) => row['Model Kodu'] || row['modelCode'])
        .map((row) => ({
          productName: row['Ürün Adı'] || row['productName'] || undefined,
          sku: row['SKU'] || row['sku'] || undefined,
          modelCode: String(row['Model Kodu'] || row['modelCode']).trim(),
        }))
        .filter((item) => item.modelCode && (item.productName || item.sku));

      if (items.length === 0) {
        toast.error(
          'Geçerli satır bulunamadı! Excel\'de "Ürün Adı" veya "SKU" ve "Model Kodu" sütunları olmalı.',
          { id: 'model-code-upload', duration: 5000 }
        );
        setModelCodeUploading(false);
        return;
      }

      toast.loading(`${items.length} ürün güncelleniyor...`, { id: 'model-code-upload' });

      const response = await axios.post('/products/bulk-update-model-code', { items });

      toast.success(
        `${response.data.updated} ürün güncellendi, ${response.data.notFound} bulunamadı`,
        { id: 'model-code-upload', duration: 5000 }
      );

      loadProducts();
      setShowModelCodeUpload(false);
    } catch (error: any) {
      console.error('Model code upload error:', error);
      toast.error(
        error.response?.data?.message || 'Model kodu yükleme başarısız!',
        { id: 'model-code-upload' }
      );
    } finally {
      setModelCodeUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadModelCodeTemplate = () => {
    const templateData = filteredProducts.map((p) => ({
      'Ürün Adı': p.productionInfo?.productName || '',
      'SKU': p.nebimInfo?.sku || '',
      'Mevcut Model Kodu': p.productionInfo?.modelCode || '',
      'Model Kodu': '',
    }));

    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [{ wch: 35 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Model Kodları');
    XLSX.writeFile(wb, `model_kodu_sablonu_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Şablon indirildi! "Model Kodu" sütununu doldurup yükleyin.');
  };

  const handleExportExcel = () => {
    try {
      // CSV formatında veri hazırla - Her varyant için ayrı satır
      const headers = [
        'ID',
        'Ürün Adı',
        'Üretici',
        'Ana SKU',
        'Renk Kodu',
        'Renk Adı',
        'Beden',
        'Varyant SKU',
        'Barkod',
        'Adet',
        'Durum',
        'Kategori',
        'Fiyat',
        'İndirimli Fiyat',
        'Kumaş Bilgisi',
        'Sevk Adeti',
        'Sevk Tarihi',
        'Alakasyon',
        'Oluşturma Tarihi',
        'Güncelleme Tarihi',
        'RPT Ürün'
      ];
      
      const rows: any[] = [];
      
      filteredProducts.forEach(p => {
        const category = [
          p.categoryInfo?.mainCategory,
          p.categoryInfo?.subCategory,
          p.categoryInfo?.detailCategory
        ].filter(Boolean).join(' > ');
        
        // Alakasyon bilgilerini formatla
        const allocations = p.productionInfo?.allocationQuantities
          ? Object.entries(p.productionInfo.allocationQuantities).map(([customer, sizeAlloc]) => {
              if (typeof sizeAlloc === 'object') {
                const allocationSizes = (sizeAlloc as any).sizes || sizeAlloc;
                const total = Object.values(allocationSizes as Record<string, number>).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
                const sizes = sortSizeEntries(allocationSizes as Record<string, number>)
                  .filter(([_, qty]) => qty > 0)
                  .map(([size, qty]) => `${size}:${qty}`)
                  .join(',');
                return `${customer}(${total} adet)[${sizes}]`;
              }
              return '';
            }).join(' | ')
          : '';
        
        // Sevk tarihini formatla
        const shippingDate = p.productionInfo?.shippingDate
          ? new Date(p.productionInfo.shippingDate).toLocaleDateString('tr-TR')
          : '';
        
        // Çoklu renk varsa, her renk + beden için ayrı satır
        if (p.nebimInfo?.colorVariantBarcodes && Object.keys(p.nebimInfo.colorVariantBarcodes).length > 0) {
          Object.entries(p.nebimInfo.colorVariantBarcodes).forEach(([colorCode, sizeBarcodesMap]) => {
            // Bu renk kodu için renk adını bul
            const colorVariant = p.productionInfo?.colors?.find(c => c.code === colorCode);
            const colorName = colorVariant?.name || '';
            
            // Her beden için ayrı satır
            sortSizeEntries(sizeBarcodesMap).forEach(([size, barcode]) => {
              const sizeQuantity = p.productionInfo?.sizeDistribution?.[size] || 0;
              const shippingQuantity = p.productionInfo?.shippingQuantities?.[size] || 0;
              
              // Varyant SKU: Ana SKU + Renk Kodu + Beden
              const variantSku = `${p.nebimInfo?.sku || ''}-${colorCode}-${size}`;
              
              rows.push([
                p._id,
                p.productionInfo?.productName || '',
                p.productionInfo?.manufacturer || '',
                p.nebimInfo?.sku || '',
                colorCode,
                colorName,
                size,
                variantSku,
                barcode || '',
                sizeQuantity,
                p.status,
                category || '',
                p.productionInfo?.salePrice || '',
                p.productionInfo?.discountPrice || '',
                p.productionInfo?.fabricInfo || '',
                shippingQuantity,
                shippingDate,
                allocations,
                new Date(p.createdAt).toLocaleDateString('tr-TR'),
                new Date(p.updatedAt).toLocaleDateString('tr-TR'),
                p.isRPT === true ? 'Evet' : 'Hayır'
              ]);
            });
          });
        }
        // Tek renk (eski format) - variantBarcodes kullanılıyor
        else if (p.nebimInfo?.variantBarcodes && Object.keys(p.nebimInfo.variantBarcodes).length > 0) {
          const colorCode = p.productionInfo?.colorCode || p.productionInfo?.colors?.[0]?.code || '';
          const colorName = p.productionInfo?.colorName || p.productionInfo?.colors?.[0]?.name || '';
          
          sortSizeEntries(p.nebimInfo.variantBarcodes).forEach(([size, barcode]) => {
            const sizeQuantity = p.productionInfo?.sizeDistribution?.[size] || 0;
            const shippingQuantity = p.productionInfo?.shippingQuantities?.[size] || 0;
            
            // Varyant SKU: Ana SKU + Beden (renk kodu yoksa)
            const variantSku = colorCode 
              ? `${p.nebimInfo?.sku || ''}-${colorCode}-${size}`
              : `${p.nebimInfo?.sku || ''}-${size}`;
            
            rows.push([
              p._id,
              p.productionInfo?.productName || '',
              p.productionInfo?.manufacturer || '',
              p.nebimInfo?.sku || '',
              colorCode,
              colorName,
              size,
              variantSku,
              barcode || '',
              sizeQuantity,
              p.status,
              category || '',
              p.productionInfo?.salePrice || '',
              p.productionInfo?.discountPrice || '',
              p.productionInfo?.fabricInfo || '',
              shippingQuantity,
              shippingDate,
              allocations,
              new Date(p.createdAt).toLocaleDateString('tr-TR'),
              new Date(p.updatedAt).toLocaleDateString('tr-TR'),
              p.isRPT === true ? 'Evet' : 'Hayır'
            ]);
          });
        }
        // Barkod yoksa sadece beden dağılımı varsa
        else if (p.productionInfo?.sizeDistribution && Object.keys(p.productionInfo.sizeDistribution).length > 0) {
          const colorCode = p.productionInfo?.colorCode || p.productionInfo?.colors?.[0]?.code || '';
          const colorName = p.productionInfo?.colorName || p.productionInfo?.colors?.[0]?.name || '';
          
          sortSizeEntries(p.productionInfo.sizeDistribution).forEach(([size, quantity]) => {
            const shippingQuantity = p.productionInfo?.shippingQuantities?.[size] || 0;
            
            const variantSku = colorCode 
              ? `${p.nebimInfo?.sku || ''}-${colorCode}-${size}`
              : `${p.nebimInfo?.sku || ''}-${size}`;
            
            rows.push([
              p._id,
              p.productionInfo?.productName || '',
              p.productionInfo?.manufacturer || '',
              p.nebimInfo?.sku || '',
              colorCode,
              colorName,
              size,
              variantSku,
              '', // Barkod yok
              quantity,
              p.status,
              category || '',
              p.productionInfo?.salePrice || '',
              p.productionInfo?.discountPrice || '',
              p.productionInfo?.fabricInfo || '',
              shippingQuantity,
              shippingDate,
              allocations,
              new Date(p.createdAt).toLocaleDateString('tr-TR'),
              new Date(p.updatedAt).toLocaleDateString('tr-TR'),
              p.isRPT === true ? 'Evet' : 'Hayır'
            ]);
          });
        }
        // Hiç varyant yoksa ana ürün bilgisi
        else {
          const colorCode = p.productionInfo?.colorCode || p.productionInfo?.colors?.[0]?.code || '';
          const colorName = p.productionInfo?.colorName || p.productionInfo?.colors?.[0]?.name || '';
          
          rows.push([
            p._id,
            p.productionInfo?.productName || '',
            p.productionInfo?.manufacturer || '',
            p.nebimInfo?.sku || '',
            colorCode,
            colorName,
            '', // Beden yok
            p.nebimInfo?.sku || '', // Varyant SKU = Ana SKU
            '', // Barkod yok
            '', // Adet yok
            p.status,
            category || '',
            p.productionInfo?.salePrice || '',
            p.productionInfo?.discountPrice || '',
            p.productionInfo?.fabricInfo || '',
            '', // Sevk adeti yok
            shippingDate,
            allocations,
            new Date(p.createdAt).toLocaleDateString('tr-TR'),
            new Date(p.updatedAt).toLocaleDateString('tr-TR'),
            p.isRPT === true ? 'Evet' : 'Hayır'
          ]);
        }
      });
      
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join('\t'),
        ...rows.map(row => row.join('\t'))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `urunler_varyantlar_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Excel dosyası indirildi! (${rows.length} varyant satırı)`);
    } catch (error) {
      toast.error('Excel export başarısız!');
      console.error('Excel export error:', error);
    }
  };

  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content_entry: products.filter(p => p.status === 'content_entry').length,
    category_setup: products.filter(p => p.status === 'category_setup').length,
    // "Görsel" kartı: sadece gerçek image_upload (legacy görseli-biten Publish Bekliyor'da sayılır)
    image_upload: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    // TODO: migration sonrası image_upload+uploadedExternally OR koşulu kaldırılacak
    publish_pending: products.filter(p =>
      p.status === 'publish_pending' ||
      (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
    ).length,
    // "Yayında" = gerçekten canlıda olanlar (kapatılmış isActive=false hariç)
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  const handleCardClick = (status: string) => {
    if (filters.department === status) {
      setFilters(prev => ({ ...prev, department: '' }));
    } else {
      setFilters(prev => ({ ...prev, department: status }));
    }
  };

  return (
    <AppLayout
      title="Admin Yönetim Paneli"
      subtitle="Tüm Ürünleri Görüntüle ve Yönet"
      syncing={syncing}
      aiScanning={aiScanning}
      aiStats={aiStats}
      onTicimaxSync={handleTicimaxSync}
      onOpenColorModal={openColorModal}
      onAIScan={handleAIScan}
      onAIScanUncontrolled={handleAIScanUncontrolled}
      onModelCodeUpload={() => setShowModelCodeUpload(true)}
    >
      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* Action Buttons - Sağ tarafta */}
        <div className="flex items-center justify-end">
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={handleExportExcel}
              className="flex items-center gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel'e Aktar
            </Button>
            
            {/* Yeni Ürün Ekle butonu SADECE admin için */}
            {user?.role === 'admin' && (
              <Button
                onClick={() => {
                  setShowModal(true);
                }}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Yeni Ürün Ekle
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Hiyerarşi Adımları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'} (SKU/Barkod)</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'content_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik</CardTitle>
              <Badge variant="secondary">{stats.content_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>
          
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'image_upload' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('image_upload')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Görsel</CardTitle>
              <Badge variant="secondary">{stats.image_upload}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('visual') || 'Görsel'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'publish_pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('publish_pending')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayın Bekliyor</CardTitle>
              <Badge variant="secondary">{stats.publish_pending}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('publish') || 'Yayın'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayında</CardTitle>
              <Badge variant="secondary">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Canlı Ürünler</p>
            </CardContent>
          </Card>
        </div>

        {/* AI Tutarsızlık Analizi Kartı */}
        {user?.role === 'admin' && (
          <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Brain className="h-5 w-5" />
                AI Tutarsızlık Analizi
                {loadingAiStats && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 ml-2"></div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <p className="text-2xl font-bold text-gray-800">{aiStats.totalProducts}</p>
                  <p className="text-xs text-gray-600">Toplam Ürün</p>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{aiStats.scannedProducts}</p>
                  <p className="text-xs text-gray-600">Taranan</p>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{aiStats.consistentProducts}</p>
                  <p className="text-xs text-gray-600 flex items-center justify-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Tutarlı
                  </p>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{aiStats.inconsistentProducts}</p>
                  <p className="text-xs text-gray-600 flex items-center justify-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Tutarsız
                  </p>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{aiStats.averageScore.toFixed(1)}%</p>
                  <p className="text-xs text-gray-600">Ort. Skor</p>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{aiStats.inconsistencyRate.toFixed(1)}%</p>
                  <p className="text-xs text-gray-600">Tutarsızlık Oranı</p>
                </div>
              </div>
              {aiStats.lastScanDate && (
                <p className="text-xs text-gray-500 mt-3 text-right">
                  Son tarama: {new Date(aiStats.lastScanDate).toLocaleString('tr-TR')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filter Bar */}
        <FilterBar 
          filters={filters}
          onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          products={products}
        />

        {/* Product Table */}
        <ProductTable
          products={filteredProducts}
          onRefresh={loadProducts}
          onSentToProducer={handleSentToProducer}
          onPublish={handlePublish}
          onReject={handleReject}
          onClosureRequest={handleClosureRequest}
          onDelete={handleDeleteClick}
          showAllActions={true}
        />
      </div>

      {/* Product Modal */}
      {showModal && (
        <ProductModal
          onClose={() => {
            setShowModal(false);
          }}
          onSubmit={async (productData) => {
            try {
              await axios.post('/products', productData);
              toast.success('Ürün başarıyla oluşturuldu!');
              setShowModal(false);
              loadProducts();
            } catch (error) {
              toast.error('Ürün oluşturulurken hata oluştu!');
              throw error;
            }
          }}
        />
      )}

      {/* Üreticiye Gönderim Modalı */}
      {showSentToProducerModal && selectedProductForProducer && (
        <SentToProducerModal
          onClose={() => {
            setShowSentToProducerModal(false);
            setSelectedProductForProducer(null);
          }}
          onSubmit={handleSentToProducerSubmit}
          productName={selectedProductForProducer.productionInfo?.productName || 'Ürün'}
          initialPurchaseCode={selectedProductForProducer.productionInfo?.purchaseCode || ''}
          initialReferenceSku={selectedProductForProducer.productionInfo?.referenceSku || ''}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && productToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Ürün Silme Onayı
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ Bu işlem geri alınamaz!
                </p>
                <p className="text-sm text-red-700">
                  Aşağıdaki ürünü silmek istediğinizden emin misiniz?
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Ürün ID:</span>
                  <Badge variant="outline">{productToDelete._id}</Badge>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Ürün Adı:</span>
                  <p className="text-sm font-semibold mt-1">
                    {productToDelete.productionInfo?.productName || 'İsimsiz Ürün'}
                  </p>
                </div>
                {productToDelete.nebimInfo?.sku && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">SKU:</span>
                    <p className="text-sm font-mono mt-1">{productToDelete.nebimInfo.sku}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDeleteCancel}
                >
                  İptal
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleDeleteConfirm}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Evet, Sil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Renk Yönetimi Modal */}
      {showColorModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
              <div>
                <CardTitle className="text-xl">Renk Varyantları Yönetimi</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Ürün girişinde kullanılacak renkleri yönetin
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowColorModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Yeni Renk Formu */}
              <form onSubmit={handleColorSubmit} className="flex gap-3 items-end p-4 bg-muted/30 rounded-lg">
                <div className="flex-1">
                  <label className="text-xs font-medium block mb-1">Renk Kodu *</label>
                  <input
                    type="text"
                    value={colorFormData.code}
                    onChange={(e) => setColorFormData({...colorFormData, code: e.target.value.toUpperCase()})}
                    required
                    placeholder="Örn: 001"
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium block mb-1">Renk Adı *</label>
                  <input
                    type="text"
                    value={colorFormData.name}
                    onChange={(e) => setColorFormData({...colorFormData, name: e.target.value})}
                    required
                    placeholder="Örn: Siyah"
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <div className="w-24">
                  <label className="text-xs font-medium block mb-1">Sıra</label>
                  <input
                    type="number"
                    value={colorFormData.sortOrder}
                    onChange={(e) => setColorFormData({...colorFormData, sortOrder: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <Button type="submit" size="sm">
                  {editingColor ? 'Güncelle' : 'Ekle'}
                </Button>
                {editingColor && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setEditingColor(null);
                      setColorFormData({ code: '', name: '', sortOrder: 0 });
                    }}
                  >
                    İptal
                  </Button>
                )}
              </form>

              {/* Renk Listesi */}
              <div className="border rounded-lg">
                <div className="flex items-center justify-between p-3 border-b bg-muted/20">
                  <span className="text-sm font-medium">Mevcut Renkler ({colors.length})</span>
                  <div className="flex gap-2">
                    {/* Gizli file input */}
                    <input
                      type="file"
                      id="colorFileInput"
                      accept=".csv,.txt,.xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('colorFileInput')?.click()}
                      disabled={importing}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      {importing ? 'Yükleniyor...' : 'Excel/CSV Yükle'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSeedColors}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Varsayılanları Yükle
                    </Button>
                  </div>
                </div>
                
                {loadingColors ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : colors.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Palette className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Henüz renk tanımlanmamış</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={handleSeedColors}
                      className="mt-2"
                    >
                      Varsayılan renkleri yükle
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y max-h-[400px] overflow-y-auto">
                    {colors.map((color, index) => (
                      <div 
                        key={color._id} 
                        className={`flex items-center justify-between p-3 hover:bg-muted/30 ${!color.isActive ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground w-6">{index + 1}</span>
                          <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{color.code}</span>
                          <span className="font-medium">{color.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={color.isActive ? "default" : "secondary"}
                            className="cursor-pointer"
                            onClick={() => handleColorToggleActive(color)}
                          >
                            {color.isActive ? 'Aktif' : 'Pasif'}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleColorEdit(color)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleColorDelete(color._id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Kategori Yönetimi Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
              <div>
                <CardTitle className="text-xl">Alt Kategori Yönetimi</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Ürün girişinde kullanılacak alt kategorileri yönetin
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCategoryModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Yeni Kategori Formu */}
              <form onSubmit={handleCategorySubmit} className="flex gap-3 items-end p-4 bg-muted/30 rounded-lg flex-wrap">
                <div className="flex-1 min-w-[100px]">
                  <label className="text-xs font-medium block mb-1">Alt Grup Kodu *</label>
                  <input
                    type="text"
                    value={categoryFormData.code}
                    onChange={(e) => setCategoryFormData({...categoryFormData, code: e.target.value})}
                    required
                    placeholder="Örn: 5"
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <div className="flex-[2] min-w-[150px]">
                  <label className="text-xs font-medium block mb-1">Alt Grup Adı *</label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                    required
                    placeholder="Örn: TRİKO"
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <div className="flex-1 min-w-[100px]">
                  <label className="text-xs font-medium block mb-1">Ürün Kodu Param</label>
                  <input
                    type="text"
                    value={categoryFormData.productCodeParam}
                    onChange={(e) => setCategoryFormData({...categoryFormData, productCodeParam: e.target.value})}
                    placeholder="Örn: 01"
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <div className="w-20">
                  <label className="text-xs font-medium block mb-1">Sıra</label>
                  <input
                    type="number"
                    value={categoryFormData.sortOrder}
                    onChange={(e) => setCategoryFormData({...categoryFormData, sortOrder: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  />
                </div>
                <Button type="submit" size="sm">
                  {editingCategory ? 'Güncelle' : 'Ekle'}
                </Button>
                {editingCategory && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryFormData({ code: '', name: '', productCodeParam: '', sortOrder: 0 });
                    }}
                  >
                    İptal
                  </Button>
                )}
              </form>

              {/* Kategori Listesi */}
              <div className="border rounded-lg">
                <div className="flex items-center justify-between p-3 border-b bg-muted/20">
                  <span className="text-sm font-medium">Mevcut Kategoriler ({categories.length})</span>
                  <div className="flex gap-2">
                    {/* Gizli file input */}
                    <input
                      type="file"
                      id="categoryFileInput"
                      accept=".csv,.txt,.xlsx,.xls"
                      onChange={handleCategoryFileUpload}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('categoryFileInput')?.click()}
                      disabled={importingCategories}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      {importingCategories ? 'Yükleniyor...' : 'Excel/CSV Yükle'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSeedCategories}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Varsayılanları Yükle
                    </Button>
                  </div>
                </div>
                
                {loadingCategories ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FolderTree className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Henüz kategori tanımlanmamış</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={handleSeedCategories}
                      className="mt-2"
                    >
                      Varsayılan kategorileri yükle
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y max-h-[400px] overflow-y-auto">
                    {/* Tablo Başlıkları */}
                    <div className="flex items-center p-3 bg-muted/10 text-xs font-medium text-muted-foreground">
                      <span className="w-8">#</span>
                      <span className="w-20">Kod</span>
                      <span className="flex-1">Alt Grup Adı</span>
                      <span className="w-24">Ürün Kodu</span>
                      <span className="w-16 text-center">Durum</span>
                      <span className="w-24 text-right">İşlemler</span>
                    </div>
                    {categories.map((category, index) => (
                      <div 
                        key={category._id} 
                        className={`flex items-center p-3 hover:bg-muted/30 ${!category.isActive ? 'opacity-50' : ''}`}
                      >
                        <span className="text-xs text-muted-foreground w-8">{index + 1}</span>
                        <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded w-20">{category.code}</span>
                        <span className="font-medium flex-1">{category.name}</span>
                        <span className="font-mono text-sm text-muted-foreground w-24">
                          {category.productCodeParam || '-'}
                        </span>
                        <div className="w-16 flex justify-center">
                          <Badge 
                            variant={category.isActive ? "default" : "secondary"}
                            className="cursor-pointer text-xs"
                            onClick={() => handleCategoryToggleActive(category)}
                          >
                            {category.isActive ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 w-24 justify-end">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleCategoryEdit(category)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleCategoryDelete(category._id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Yardım Metni */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p className="font-medium mb-1">📋 Excel/CSV Format:</p>
                <p>Alt Grup Kodu | Alt Grup Adı | Ürün Kodu Parametresi</p>
                <p className="text-xs mt-1 text-amber-600">Örnek: 5 | TRİKO | 01</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Model Kodu Excel Yükleme Modalı */}
      {showModelCodeUpload && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Upload className="h-5 w-5 text-purple-600" />
                  Model Kodu Toplu Yükleme
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowModelCodeUpload(false)}
                  disabled={modelCodeUploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-blue-900">Nasıl Çalışır?</p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Önce şablonu indirin (mevcut ürünler listeli)</li>
                  <li>"Model Kodu" sütununu doldurun</li>
                  <li>Dosyayı yükleyin</li>
                </ol>
                <p className="text-xs text-blue-700 mt-2">
                  Eşleştirme "Ürün Adı" veya "SKU" üzerinden yapılır.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={handleDownloadModelCodeTemplate}
                  className="w-full gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Şablon İndir ({filteredProducts.length} ürün)
                </Button>

                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleModelCodeExcelUpload}
                    disabled={modelCodeUploading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <Button
                    className="w-full gap-2 bg-purple-600 hover:bg-purple-700"
                    disabled={modelCodeUploading}
                  >
                    {modelCodeUploading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {modelCodeUploading ? 'Yükleniyor...' : 'Excel Dosyası Yükle'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AppLayout>
  );
};

export default Dashboard;
